import { MouseEvent, useSyncExternalStore } from 'react';

// Moving between the home page and the app without a page load. The address says which one shows
// (src/root.tsx): the app when it names a dataset. Links are real links, so that they also work
// opened in a new tab and are followed by crawlers; a plain click is handled here instead.

const NAVIGATE_EVENT = 'all2scale:navigate';

const subscribe = (onChange: () => void) => {
	window.addEventListener('popstate', onChange);
	window.addEventListener(NAVIGATE_EVENT, onChange);
	return () => {
		window.removeEventListener('popstate', onChange);
		window.removeEventListener(NAVIGATE_EVENT, onChange);
	};
};

/** The query string of the address, kept current through navigate() and the back button. */
export const useSearch = (): string =>
	useSyncExternalStore(
		subscribe,
		() => window.location.search,
		() => ''
	);

/** Goes to `href` as a new history entry, so that the back button returns. */
export const navigate = (href: string) => {
	window.history.pushState(null, '', href);
	window.dispatchEvent(new Event(NAVIGATE_EVENT));
	window.scrollTo(0, 0);
};

// A click that the browser should handle itself: into a new tab or window, or not the main button
const opensElsewhere = (event: MouseEvent) =>
	event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

/** A link's click handler that navigates without a page load, for a plain click. */
export const followLink = (href: string, before?: () => void) => (event: MouseEvent) => {
	if (opensElsewhere(event)) return;
	event.preventDefault();
	before?.();
	navigate(href);
};
